import { useCompanyInfo } from "@/api/services/main/company";
import { createRenewVisaDocumentGroupApi, useGetRenewVisaDocumentList } from "@/api/services/main/renew-visa";
import { TGetRenewVisaDocumentListOutputDto } from "@/api/services/main/renew-visa/dtos/get-renew-visa-document-list.dto";
import { Table } from "@/components/elements/Table";
import { DATE_FORMAT } from "@/constants/datetime";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { EMPTY_STR } from "@/constants/string";
import { TaskCard } from "@/features/dashboard/components/tasks/TaskCard";
import { RenewVisaDocumentStatusTag } from "@/features/renew-visa/components/RenewVisaDocumentStatusTag";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { RENEW_VISA_DOCUMENT_STATUS } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { truncateWithEllipsis } from "@/utils/string";
import { getDynamicRoute } from "@/utils/url";
import { useQueryClient } from "@tanstack/react-query";
import { MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

export const RenewVisaTaskCard = () => {
  const queryClient = useQueryClient();

  const { data: employeeRenewVisaDocumentList, isLoading } = useGetRenewVisaDocumentList({
    page: 1,
    perPage: 4,
    sorts: ["renewVisaDocument.status|DESC", "renewVisaDocument.residenceExpiryDate|ASC"],
  });

  const { data: companyData } = useCompanyInfo();

  const supporterName = useMemo(() => {
    if (!companyData) return null;
    return companyData?.data?.supporters.length > 0 ? companyData?.data?.supporters?.[0].supporterName : null;
  }, [companyData]);

  const navigate = useNavigate();

  const columns = useMemo<MRT_ColumnDef<TGetRenewVisaDocumentListOutputDto>[]>(
    () => [
      {
        header: "更新期限",
        Cell: ({ row }) => (row.original.residenceExpiryDate ? dayjs(row.original.residenceExpiryDate).format(DATE_FORMAT) : EMPTY_STR.TEXT),
        size: 100,
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "ステータス",
        Cell: ({ row }) => {
          const { status } = row.original;
          if (!status) return EMPTY_STR.TEXT;
          return <RenewVisaDocumentStatusTag status={status} />;
        },
        size: 100,
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "外国人",
        Cell: ({ row }) => {
          const { employeeFullName } = row.original;
          return employeeFullName ? truncateWithEllipsis(employeeFullName, 26) : EMPTY_STR.TEXT;
        },
        size: 100,
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
      {
        header: "担当者",
        Cell: ({}) => {
          return supporterName ?? EMPTY_STR.TEXT;
        },
        size: 100,
        muiTableBodyCellProps: {
          sx: { minWidth: "unset" },
        },
        muiTableHeadCellProps: {
          sx: { minWidth: "unset" },
        },
      },
    ],
    [],
  );
  const { table } = useTable({
    rows: employeeRenewVisaDocumentList?.data ?? [],
    columns,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: async () => {
        const { employeeRenewVisaDocumentGroupId, status, id } = row.original;
        if (employeeRenewVisaDocumentGroupId) {
          navigate(
            [RENEW_VISA_DOCUMENT_STATUS.COMPLETE, RENEW_VISA_DOCUMENT_STATUS.WITHDRAWN, RENEW_VISA_DOCUMENT_STATUS.REJECTED].includes(status)
              ? getDynamicRoute(AppRoutes.renewVisaInfoHistoryDetail, {
                  employeeRenewVisaDocumentGroupId,
                })
              : getDynamicRoute(AppRoutes.renewVisaPresent, { employeeRenewVisaDocumentGroupId }),
          );
        } else {
          try {
            const newRenewVisaDocumentGroup = await createRenewVisaDocumentGroupApi({ renewVisaDocumentIds: [toNumber(id)] });
            await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_LIST] });
            navigate(getDynamicRoute(AppRoutes.renewVisaPresent, { employeeRenewVisaDocumentGroupId: newRenewVisaDocumentGroup.data.id }));
          } catch (error) {
            showError(error);
          }
        }
      },
      sx: { cursor: "pointer" },
    }),
  });

  return (
    <TaskCard title="在留期間更新" seeMoreHref={AppRoutes.renewVisa}>
      <Table table={table} isLoading={isLoading} />
    </TaskCard>
  );
};
